/**
 * PostHog: product analytics, session replay and error tracking
 *
 * Shared across all console apps (console, auth, marketplace, notes, platform,
 * staff). The project API key + ingestion host come from each app's runtime
 * config (public — they ship in the bundle); everything else — autocapture,
 * masked session replay, exception capture, identity and B2B group analytics —
 * is shared here. Apps reach identity through `analytics.ts`, so each one wires
 * a single set of callbacks into SessionContextProvider.
 */

import posthog from 'posthog-js';
import { prepareExceptionEvent, redactContext } from './error-events';

// import.meta.env is injected by each consuming app's Vite build; type it
// locally so this package type-checks without a Vite type dependency.
const META_ENV = (import.meta as unknown as { env: Record<string, string | undefined> })
  .env;

const DEFAULT_HOST = 'https://eu.i.posthog.com';

// Only fan identify/reset/capture out to PostHog once init has actually run
// (no key / disabled / local dev all skip init — calls then no-op cleanly).
let started = false;

export function initPostHog({
  apiKey,
  host,
  environment,
}: {
  apiKey?: string;
  host?: string;
  /** The deployed environment every exception names. */
  environment?: string;
}): void {
  const isDev = META_ENV.MODE === 'development';

  if (META_ENV.VITE_DISABLE_POSTHOG === 'true') {
    console.log('PostHog disabled via VITE_DISABLE_POSTHOG');
    return;
  }

  if (!apiKey) {
    console.log('PostHog key not configured, product analytics disabled');
    return;
  }

  posthog.init(apiKey, {
    api_host: host || DEFAULT_HOST,

    // Modern preset: capture pageviews on History API changes (our SPAs use
    // client-side routing), persist debounced, etc.
    defaults: '2025-05-24',
    capture_pageview: 'history_change',
    autocapture: true,

    // Don't burn a person profile on anonymous traffic (e.g. the login screen
    // before a session exists) — only identified users get one.
    person_profiles: 'identified_only',

    // Fully-masked session replay — PostHog is the single session recorder.
    // maskAllInputs + maskTextSelector '*': no input values or text are recorded.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
    },

    // Unhandled errors and promise rejections become error-tracking issues,
    // beside the replay, the person and the workspace of the session that hit
    // them. Set here rather than left to the project's remote setting, so a
    // toggle in PostHog cannot silently stop it.
    capture_exceptions: true,
    before_send: (event) =>
      prepareExceptionEvent(event, environment || 'local'),

    loaded: (ph) => {
      // Local `vite dev` shouldn't phone home even if a key is present.
      if (isDev) {
        ph.opt_out_capturing();
        console.log('[PostHog] development mode — capturing opted out');
      }
    },
  });

  started = true;

  if (isDev) {
    console.log('[PostHog] Initialized', { host: host || DEFAULT_HOST });
  } else {
    console.log('PostHog initialized');
  }
}

/**
 * Identify the current user for product analytics.
 *
 * Uses the stable user id as the distinct id, attaches email as a person
 * property, and associates the person with their workspace/project via PostHog
 * group analytics (so B2B funnels and retention can be sliced per tenant).
 */
export function identifyUser(user: {
  id: string;
  email?: string;
  tenantId?: string;
  projectId?: string;
}): void {
  if (!started) return;

  posthog.identify(user.id, {
    email: user.email,
    tenant_id: user.tenantId,
    project_id: user.projectId,
  });

  if (user.tenantId) {
    posthog.group('tenant', user.tenantId);
  }
  if (user.projectId) {
    posthog.group('project', user.projectId);
  }
}

/**
 * Clear identity on logout — resets the distinct id and unlinks groups so the
 * next user on this device starts a fresh anonymous session.
 */
export function resetUser(): void {
  if (!started) return;
  posthog.reset();
}

/**
 * Capture a custom product-analytics event.
 *
 */
export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!started) return;
  posthog.capture(event, properties);
}

/**
 * Capture a caught exception as an error-tracking issue.
 *
 * Use this for errors caught in try/catch blocks, error boundaries and the
 * router's catch boundary; unhandled errors are captured automatically. The
 * context rides along as properties, with credentials redacted.
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (!started) return;
  posthog.captureException(error, context ? redactContext(context) : undefined);
}

/** Escape hatch for advanced use (feature flags, surveys, manual control). */
export function getPostHog(): typeof posthog {
  return posthog;
}

/** Map a bug-report severity to a PostHog exception level for triage. */
const SEVERITY_TO_LEVEL: Record<string, string> = {
  critical: 'error',
  high: 'error',
  medium: 'warning',
  low: 'info',
};

/**
 * Capture a human-filed bug report as a PostHog error-tracking issue.
 *
 * Sent as a manual exception, so it is an issue (not just an event) beside the
 * automatic ones, correlated to the person, their session replay, events and
 * flag state. `report_type: 'bug_report'` tells the two apart, and keeps a
 * report out of the noise filter whatever its text says. The optional
 * screenshot rides along as an event property; the popover bounds its size so
 * the event stays within ingestion limits.
 *
 * Signature mirrors the `CaptureBugReport` sink so apps can inject it directly.
 */
export function captureBugReport(
  message: string,
  extras: Record<string, unknown>,
  screenshotDataUrl?: string | null,
): void {
  if (!started) {
    if (META_ENV.MODE === 'development') {
      console.log('[PostHog] Bug report skipped (not initialized):', message);
    }
    return;
  }

  const error = new Error(message);
  error.name = 'BugReport';

  posthog.captureException(error, {
    ...redactContext(extras),
    report_type: 'bug_report',
    $exception_level: SEVERITY_TO_LEVEL[String(extras.severity)] ?? 'info',
    ...(screenshotDataUrl ? { bug_screenshot: screenshotDataUrl } : {}),
  });
}
